import React, { useEffect, useRef, useState } from "react";
import MinuteStepInput from "./MinuteStepInput";
import SearchableSiteSelect from "./SearchableSiteSelect";
import "./horizonTable.css";
import "../forms/themeCheckbox.css";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDateYYYYMMDDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYYYYMMDDLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const parts = ymd.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(day)) return null;
  return new Date(y, mo - 1, day);
}

const MAX_BREAK_MINUTES = 60;
const DEFAULT_BREAK_TIME = 30;

function addDaysYMD(ymd, days) {
  const d = parseYYYYMMDDLocal(ymd);
  if (!d) return "";
  d.setDate(d.getDate() + days);
  return formatDateYYYYMMDDLocal(d);
}

function FatigueShiftsPage() {
  const [shifts, setShifts] = useState([]);
  const [workSheets, setWorkSheets] = useState([]);
  const [filterSiteId, setFilterSiteId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [createAttempted, setCreateAttempted] = useState(false);
  const [newShift, setNewShift] = useState({
    startDate: "",
    endDate: "",
    worksheetName: "",
    siteId: "",
    workerIds: [],
    status: "active",
    notes: "",
    workload: 1,
    attention: 1,
    break_frequency: 180,
    break_average_length: 15,
    continuous_work_length: 360,
    break_after_continuous_work: 30,
    break_time: DEFAULT_BREAK_TIME,
  });
  const [sites, setSites] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [workerFilter, setWorkerFilter] = useState("");
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState("");
  const workerDropdownRef = useRef(null);
  const [addWorkerDropdownOpen, setAddWorkerDropdownOpen] = useState(false);
  const [addWorkerSearch, setAddWorkerSearch] = useState("");
  const addWorkerDropdownRef = useRef(null);
  const [currentUserRole, setCurrentUserRole] = useState("");

  function getMinStartDate() {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return formatDateYYYYMMDDLocal(t);
  }

  useEffect(() => {
    fetchWorkSheets();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role) {
        setCurrentUserRole(user.role);
      }
    } catch (e) {
      console.error("Error reading user role:", e);
    }
  }, []);

  useEffect(() => {
    if (showAddModal) {
      fetchSites();
      fetchAppUsers();
    } else {
      setAddWorkerDropdownOpen(false);
      setAddWorkerSearch("");
    }
  }, [showAddModal]);

  useEffect(() => {
    fetchShifts();
  }, [filterSiteId]);

  useEffect(() => {
    if (!workerDropdownOpen) return;
    function handleClickOutside(e) {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(e.target)) setWorkerDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [workerDropdownOpen]);

  useEffect(() => {
    if (!addWorkerDropdownOpen) return;
    function handleClickOutside(e) {
      if (addWorkerDropdownRef.current && !addWorkerDropdownRef.current.contains(e.target)) setAddWorkerDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addWorkerDropdownOpen]);

  async function fetchWorkSheets() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setWorkSheets(data.success && Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchSites() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sites", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSites(data.success && Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAppUsers(data.success && Array.isArray(data.data) ? data.data : []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchShifts() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const url = getBaseUrl() + "/api/fatigue-shifts";
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        setError(`Failed to load shifts (${res.status})`);
        setShifts([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      setShifts(data.success && Array.isArray(data.data) ? data.data : []);
      setError(null);
    } catch (err) {
      setShifts([]);
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setCreateAttempted(false);
    setNewShift({
      startDate: "",
      endDate: "",
      worksheetName: "",
      siteId: "",
      workerIds: [],
      status: "active",
      notes: "",
      workload: 1,
      attention: 1,
      break_frequency: 180,
      break_average_length: 15,
      continuous_work_length: 360,
      break_after_continuous_work: 30,
      break_time: DEFAULT_BREAK_TIME,
    });
    setShowAddModal(true);
  };

  const toggleAddWorker = (userId) => {
    setNewShift((prev) => ({
      ...prev,
      workerIds: prev.workerIds.includes(userId)
        ? prev.workerIds.filter((id) => id !== userId)
        : [...prev.workerIds, userId],
    }));
  };

  const handleCreate = async () => {
    setCreateAttempted(true);
    const startDate = String(newShift.startDate || "").trim();
    const endDate = String(newShift.endDate || "").trim();
    const siteId = String(newShift.siteId || "").trim();

    if (!startDate) {
      toast.error("Start Date is required");
      return;
    }
    if (!endDate) {
      toast.error("End Date is required");
      return;
    }
    if (!siteId) {
      toast.error("Site is required");
      return;
    }
    if (endDate < startDate) {
      toast.error("End Date cannot be before Start Date");
      return;
    }
    if (!Array.isArray(newShift.workerIds) || newShift.workerIds.length === 0) {
      toast.error("Assigned Workers is required");
      return;
    }
    const breakTime = Number(newShift.break_time);
    if (!Number.isFinite(breakTime) || breakTime <= 0) {
      toast.error("Breaks (min) must be greater than 0");
      return;
    }
    if (breakTime > MAX_BREAK_MINUTES) {
      toast.error(`Breaks (min) must be ${MAX_BREAK_MINUTES} or less`);
      return;
    }
    const minStart = getMinStartDate();
    if (startDate < minStart) {
      toast.error("Start Date cannot be in the past");
      return;
    }
    setModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        siteId,
        workerIds: newShift.workerIds || [],
        name: (newShift.worksheetName || "").trim() || undefined,
        dateFrom: startDate,
        dateTo: endDate,
        status: newShift.status === "inactive" ? "inactive" : "active",
        notes: (newShift.notes || "").trim() || undefined,
        workload: newShift.workload,
        attention: newShift.attention,
        break_frequency: newShift.break_frequency,
        break_average_length: newShift.break_average_length,
        continuous_work_length: newShift.continuous_work_length,
        break_after_continuous_work: newShift.break_after_continuous_work,
        break_time: breakTime,
      };
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const sheetId = data.data?._id;
        for (const appUserId of newShift.workerIds || []) {
          await fetch(getBaseUrl() + "/api/site-worker-assignments", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ siteId: newShift.siteId, appUserId }),
          });
        }
        await fetchWorkSheets();
        setShowAddModal(false);
        toast.success("Work sheet created");
        const newSiteId = data.data?.siteId?._id || data.data?.siteId;
        if (newSiteId && filterSiteId !== String(newSiteId)) setFilterSiteId(String(newSiteId));
      } else {
        toast.error(data.message || "Failed to create work sheet");
      }
    } catch {
      toast.error("Failed to create work sheet");
    } finally {
      setModalLoading(false);
    }
  };

  const openEditModal = (shift) => {
    setEditShift({
      _id: shift._id,
      onDutyTime: shift.onDutyTime || "06:00",
      offDutyTime: shift.offDutyTime || "18:00",
      doorToDoorMinutes: shift.doorToDoorMinutes != null ? String(shift.doorToDoorMinutes) : "",
      jobTypeBreaks: shift.jobTypeBreaks || "Default",
      breaks: Array.isArray(shift.breaks) ? [...shift.breaks] : [],
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editShift || !editShift._id) return;
    setModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-shifts/" + editShift._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          onDutyTime: editShift.onDutyTime,
          offDutyTime: editShift.offDutyTime,
          doorToDoorMinutes: editShift.doorToDoorMinutes ? Number(editShift.doorToDoorMinutes) : null,
          jobTypeBreaks: editShift.jobTypeBreaks || "Default",
          breaks: editShift.breaks,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchShifts();
        setShowEditModal(false);
        setEditShift(null);
        toast.success("Shift updated");
        if (data.exceedanceFormCreated) {
          toast.warning("Rule broken — exceedance form created. Employee must fill the form. See Fatigue Management → Exceedance Forms.", { autoClose: 6000 });
        } else if (data.ruleResults && !data.ruleResults.passed && data.ruleResults.breaches?.length) {
          toast.warning(`Fatigue rules: ${data.ruleResults.level} — ${data.ruleResults.breaches.length} breach(es). Check FRI Schedule on Work Sheet.`);
        }
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-shifts/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchShifts();
        toast.success("Shift deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getWorkloadLabel = (v) => {
    const n = v == null ? null : Number(v);
    if (n === 0) return "Extremely demanding, no spare capacity";
    if (n === 1) return "Moderately demanding, little spare capacity";
    if (n === 2) return "Moderately undemanding, some spare capacity";
    if (n === 3) return "Extremely undemanding, lots of spare capacity";
    return "—";
  };

  const getAttentionLabel = (v) => {
    const n = v == null ? null : Number(v);
    if (n === 0) return "All or nearly all the time";
    if (n === 1) return "Most of the Time";
    if (n === 2) return "Some of the time";
    if (n === 3) return "Rarely or Nearly none of the time";
    return "—";
  };

  const siteMap = new Map();
  workSheets.forEach((ws) => {
    const id = ws.siteId?._id || ws.siteId;
    if (!id) return;
    const name = ws.siteId?.siteName || "—";
    if (!siteMap.has(String(id))) siteMap.set(String(id), { siteId: String(id), siteName: name });
  });
  const uniqueSites = Array.from(siteMap.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));

  const currentSheet = workSheets[0];
  const workersInSheet = currentSheet && Array.isArray(currentSheet.workerIds) ? currentSheet.workerIds : [];

  const workerOptions = Array.from(
    new Set(
      workSheets
        .flatMap((ws) =>
          Array.isArray(ws.workerIds)
            ? ws.workerIds.map((w) =>
                w && typeof w === "object"
                  ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || ""
                  : ""
              )
            : []
        )
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  let displayWorkSheets = filterSiteId
    ? workSheets.filter((ws) => String(ws.siteId?._id || ws.siteId) === String(filterSiteId))
    : workSheets;

  if (workerFilter.trim()) {
    const term = workerFilter.trim().toLowerCase();
    displayWorkSheets = displayWorkSheets.filter((ws) => {
      if (!Array.isArray(ws.workerIds) || ws.workerIds.length === 0) return false;
      const names = ws.workerIds
        .map((w) =>
          w && typeof w === "object"
            ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || ""
            : ""
        )
        .filter(Boolean)
        .join(", ")
        .toLowerCase();
      return names.includes(term);
    });
  }

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
          Shift Creation
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ minWidth: "220px" }}>
            <SearchableSiteSelect
              value={filterSiteId}
              onChange={(v) => setFilterSiteId(v)}
              options={uniqueSites.map((s) => ({ value: s.siteId, label: s.siteName }))}
              placeholder="All sites"
            />
          </div>
          <div ref={workerDropdownRef} style={{ position: "relative", minWidth: "220px" }}>
            <input
              type="text"
              value={workerSearch}
              onFocus={() => setWorkerDropdownOpen(true)}
              onChange={(e) => {
                const val = e.target.value;
                setWorkerSearch(val);
                setWorkerFilter(val);
                setWorkerDropdownOpen(true);
              }}
              placeholder="Search worker"
              style={{
                padding: "8px 32px 8px 12px",
                borderRadius: "8px",
                border: workerDropdownOpen ? "2px solid #8b1634" : "1px solid #e2e8f0",
                fontSize: "14px",
                minWidth: "220px",
                outline: "none",
                boxShadow: workerDropdownOpen ? "0 0 0 1px rgba(139, 22, 52, 0.1)" : "none",
              }}
            />
            {(workerFilter || workerSearch) && (
              <button
                type="button"
                onClick={() => {
                  setWorkerFilter("");
                  setWorkerSearch("");
                  setWorkerDropdownOpen(false);
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  color: "#4a5568",
                  fontSize: "14px",
                }}
              >
                ×
              </button>
            )}
            {workerDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: "4px",
                  background: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
                  maxHeight: "260px",
                  overflowY: "auto",
                  zIndex: 20,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {workerOptions
                  .filter((name) =>
                    !workerSearch
                      ? true
                      : name.toLowerCase().includes(workerSearch.toLowerCase())
                  )
                  .map((name) => (
                    <div
                      key={name}
                      onClick={() => {
                        setWorkerFilter(name);
                        setWorkerSearch(name);
                        setWorkerDropdownOpen(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#1a202c",
                        borderBottom: "1px solid #f1f5f9",
                        background:
                          workerFilter === name ? "rgba(139, 22, 52, 0.08)" : "#ffffff",
                      }}
                    >
                      {name}
                    </div>
                  ))}
                {workerOptions.length === 0 && (
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      color: "#a0aec0",
                    }}
                  >
                    No workers found
                  </div>
                )}
              </div>
            )}
          </div>
          {(filterSiteId || workerFilter) && (
            <button
              type="button"
              onClick={() => {
                setFilterSiteId("");
                setWorkerFilter("");
                setWorkerSearch("");
                setWorkerDropdownOpen(false);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "none",
                background: "#e53e3e",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          )}
          {(currentUserRole || "").toLowerCase() === "manager" && (
            <button
              type="button"
              onClick={openAddModal}
              style={{
                background: "#8b1634",
                color: "#fff",
                fontWeight: "600",
                padding: "10px 20px",
                borderRadius: "12px",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-plus" /> Create Shift
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "0.85fr 0.85fr 1fr 0.9fr 1.1fr 0.95fr 0.95fr 0.85fr 0.85fr 0.95fr 0.95fr 0.7fr 0.9fr 1.2fr",
            minWidth: "1400px",
            background: "#f7fafc",
            padding: "12px 16px",
            borderBottom: "1px solid #e2e8f0",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Start Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>End Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Worksheet Name</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Assigned Workers</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Workload</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Attention</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Break every (min)</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Break length (min)</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Max continuous (min)</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Break after (min)</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Status</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Updated Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Notes</div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#8b1634", fontWeight: "600" }}>
            Loading...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#e53e3e" }}>{error}</div>
        ) : workSheets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#718096" }}>
            No data. Use Add Shift to create a work sheet.
          </div>
        ) : displayWorkSheets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#718096" }}>
            No work sheet matches the selected filter.
          </div>
        ) : (
          displayWorkSheets.map((ws, idx) => (
            <div
              key={"ws-" + (ws._id || idx)}
              style={{
                display: "grid",
                gridTemplateColumns: "0.85fr 0.85fr 1fr 0.9fr 1.1fr 0.95fr 0.95fr 0.85fr 0.85fr 0.95fr 0.95fr 0.7fr 0.9fr 1.2fr",
                minWidth: "1400px",
                padding: "16px",
                borderBottom: "1px solid #f1f5f9",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(ws.dateFrom)}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(ws.dateTo)}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.name || "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>
                {ws.siteId?.siteName ? (ws.siteId.jNumber ? `${ws.siteId.siteName} (${ws.siteId.jNumber})` : ws.siteId.siteName) : "—"}
              </div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>
                {Array.isArray(ws.workerIds) && ws.workerIds.length > 0
                  ? (() => {
                      const names = ws.workerIds.map((w) =>
                        w && typeof w === "object"
                          ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || ""
                          : ""
                      ).filter(Boolean);
                      return names.length > 0 ? names.join(", ") : "—";
                    })()
                  : "—"}
              </div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{getWorkloadLabel(ws.workload)}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{getAttentionLabel(ws.attention)}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.break_frequency != null ? `${ws.break_frequency} min` : "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.break_average_length != null ? `${ws.break_average_length} min` : "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.continuous_work_length != null ? `${ws.continuous_work_length} min` : "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.break_after_continuous_work != null ? `${ws.break_after_continuous_work} min` : "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{ws.status || "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(ws.updatedAt || ws.createdAt)}</div>
              <div
                style={{
                  color: "#2d3748",
                  fontSize: "14px",
                  maxWidth: "220px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={ws.notes || ""}
              >
                {ws.notes || "—"}
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "16px 20px", borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: "#8b1634", fontSize: "18px" }}>Create Shift</h5>
                <button type="button" className="btn-close" onClick={() => { setShowAddModal(false); setCreateAttempted(false); }} style={{ filter: "none", opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto", padding: "24px 20px", background: "#fff" }}>
                <div className="row mb-3">
                  <div className="col-12">
                    <p
                      style={{
                        margin: 0,
                        padding: "10px 12px",
                        background: "#fee2e2",
                        border: "1px solid #fca5a5",
                        borderRadius: "8px",
                        color: "#dc2626",
                        fontSize: "13px",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                      }}
                    >
                      DEFAULT INFORMATION BELOW - DO NOT CHANGE UNLESS SPECIFICALLY REQUIRED
                    </p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Start Date *</label>
                    <DatePicker
                      selected={parseYYYYMMDDLocal(newShift.startDate)}
                      onChange={(d) => {
                        const startDate = d ? formatDateYYYYMMDDLocal(d) : "";
                        setNewShift((prev) => {
                          let endDate = prev.endDate;
                          if (startDate && endDate && endDate < startDate) endDate = "";
                          return { ...prev, startDate, endDate };
                        });
                      }}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      wrapperClassName="w-100"
                      placeholderText="Select date"
                      minDate={parseYYYYMMDDLocal(getMinStartDate())}
                      popperProps={{ style: { zIndex: 1060 } }}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>End Date *</label>
                    <DatePicker
                      selected={parseYYYYMMDDLocal(newShift.endDate)}
                      onChange={(d) =>
                        setNewShift((prev) => ({ ...prev, endDate: d ? formatDateYYYYMMDDLocal(d) : "" }))
                      }
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      wrapperClassName="w-100"
                      placeholderText="Select date"
                      minDate={
                        newShift.startDate
                          ? parseYYYYMMDDLocal(newShift.startDate)
                          : parseYYYYMMDDLocal(getMinStartDate())
                      }
                      popperProps={{ style: { zIndex: 1060 } }}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Worksheet Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Week 1 - Phase 16"
                    value={newShift.worksheetName}
                    onChange={(e) => setNewShift({ ...newShift, worksheetName: e.target.value })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Site *</label>
                  <SearchableSiteSelect
                    value={newShift.siteId}
                    onChange={(v) => setNewShift({ ...newShift, siteId: v })}
                    options={sites.map((s) => ({ value: s._id, label: `${s.siteName}${s.jNumber ? ` (${s.jNumber})` : ""}` }))}
                    placeholder="Select site..."
                  />
                </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Assigned Workers *</label>
                  {appUsers.length === 0 ? (
                    <p className="text-muted small mb-0">No app users. Add users in App Users first.</p>
                  ) : (
                      <div ref={addWorkerDropdownRef} style={{ position: "relative" }}>
                        <input
                          type="text"
                          placeholder="Search workers..."
                          value={addWorkerSearch}
                          onChange={(e) => setAddWorkerSearch(e.target.value)}
                          onFocus={() => setAddWorkerDropdownOpen(true)}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            border:
                              createAttempted && (!Array.isArray(newShift.workerIds) || newShift.workerIds.length === 0)
                                ? "2px solid #e53e3e"
                                : addWorkerDropdownOpen
                                  ? "2px solid #8b1634"
                                  : "1px solid #e2e8f0",
                            fontSize: "14px",
                            outline: "none",
                            boxShadow: addWorkerDropdownOpen ? "0 0 0 2px rgba(139,22,52,0.15)" : "none",
                          }}
                        />
                        {createAttempted && (!Array.isArray(newShift.workerIds) || newShift.workerIds.length === 0) ? (
                          <div style={{ marginTop: "6px", fontSize: "12px", fontWeight: 600, color: "#e53e3e" }}>
                            Please select at least one worker.
                          </div>
                        ) : null}
                        {newShift.workerIds.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                            {newShift.workerIds.map((id) => {
                              const w = appUsers.find((u) => u._id === id);
                              return w ? (
                                <span
                                  key={id}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "4px 8px",
                                    background: "rgba(139, 22, 52, 0.1)",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    color: "#8b1634",
                                  }}
                                >
                                  {w.firstName} {w.lastName}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleAddWorker(id); }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: "0 2px",
                                      color: "#8b1634",
                                      fontSize: "14px",
                                      lineHeight: 1,
                                    }}
                                  >
                                    <i className="fa fa-times" />
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        {addWorkerDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              marginTop: "4px",
                              background: "#fff",
                              borderRadius: "8px",
                              border: "1px solid #8b1634",
                              boxShadow: "0 10px 25px rgba(139,22,52,0.12)",
                              zIndex: 1050,
                              overflow: "hidden",
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                              {appUsers
                                .filter((w) =>
                                  !addWorkerSearch
                                    ? true
                                    : `${w.firstName || ""} ${w.lastName || ""} ${w.email || ""}`.toLowerCase().includes(addWorkerSearch.toLowerCase())
                                )
                                .map((w) => (
                                  <div
                                    key={w._id}
                                    onClick={() => { toggleAddWorker(w._id); setCreateAttempted(false); }}
                                    style={{
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      background: newShift.workerIds.includes(w._id) ? "rgba(139, 22, 52, 0.08)" : "transparent",
                                      borderBottom: "1px solid #f1f5f9",
                                    }}
                                  >
                          <input
                            type="checkbox"
                            className="form-check-input theme-checkbox"
                            checked={newShift.workerIds.includes(w._id)}
                            onChange={() => {}}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAddWorker(w._id);
                              setCreateAttempted(false);
                            }}
                            style={{ margin: 0, cursor: "pointer", flexShrink: 0 }}
                          />
                                    <span>{w.firstName} {w.lastName}</span>
                        </div>
                      ))}
                            </div>
                    </div>
                  )}
                </div>
                    )}
                </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Workload</label>
                    <select
                      className="form-select"
                      value={newShift.workload}
                      onChange={(e) => setNewShift({ ...newShift, workload: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    >
                      <option value={0}>Extremely demanding, no spare capacity</option>
                      <option value={1}>Moderately demanding, little spare capacity</option>
                      <option value={2}>Moderately undemanding, some spare capacity</option>
                      <option value={3}>Extremely undemanding, lots of spare capacity</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Attention</label>
                    <select
                      className="form-select"
                      value={newShift.attention}
                      onChange={(e) => setNewShift({ ...newShift, attention: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    >
                      <option value={0}>All or nearly all the time</option>
                      <option value={1}>Most of the Time</option>
                      <option value={2}>Some of the time</option>
                      <option value={3}>Rarely or Nearly none of the time</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Breaks (min)</label>
                    <MinuteStepInput
                      id="create-shift-break-time"
                      value={newShift.break_time}
                      onChange={(break_time) => setNewShift({ ...newShift, break_time })}
                      max={MAX_BREAK_MINUTES}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break every (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={newShift.break_frequency}
                      onChange={(e) => setNewShift({ ...newShift, break_frequency: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break length (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={newShift.break_average_length}
                      onChange={(e) => setNewShift({ ...newShift, break_average_length: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Max continuous work (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={newShift.continuous_work_length}
                      onChange={(e) => setNewShift({ ...newShift, continuous_work_length: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Break after continuous work (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={newShift.break_after_continuous_work}
                      onChange={(e) => setNewShift({ ...newShift, break_after_continuous_work: Number(e.target.value) })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Optional operational context..."
                    value={newShift.notes}
                    onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", resize: "vertical" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "16px 20px", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
                <button type="button" className="btn" onClick={() => { setShowAddModal(false); setCreateAttempted(false); }} style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "8px 20px" }}>Cancel</button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: "#8b1634", color: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 24px", border: "none", boxShadow: "0 2px 8px rgba(139,22,52,0.3)" }}
                  onClick={handleCreate}
                  disabled={modalLoading}
                >
                  {modalLoading ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editShift && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "16px 20px", borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: "#8b1634", fontSize: "18px" }}>Edit Shift</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setEditShift(null); }} style={{ filter: "none", opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={{ padding: "24px 20px" }}>
                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>On duty</label>
                    <input
                      type="time"
                      className="form-control"
                      value={editShift.onDutyTime}
                      onChange={(e) => setEditShift({ ...editShift, onDutyTime: e.target.value })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Off duty</label>
                    <input
                      type="time"
                      className="form-control"
                      value={editShift.offDutyTime}
                      onChange={(e) => setEditShift({ ...editShift, offDutyTime: e.target.value })}
                      style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                      onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" }}>Door-to-door (minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    value={editShift.doorToDoorMinutes}
                    onChange={(e) => setEditShift({ ...editShift, doorToDoorMinutes: e.target.value })}
                    style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
                    onFocus={(e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "16px 20px", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
                <button type="button" className="btn" onClick={() => { setShowEditModal(false); setEditShift(null); }} style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "8px 20px" }}>Cancel</button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: "#8b1634", color: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 24px", border: "none", boxShadow: "0 2px 8px rgba(139,22,52,0.3)" }}
                  onClick={handleEditSave}
                  disabled={modalLoading}
                >
                  {modalLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Shift</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this shift?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FatigueShiftsPage;
